import { useSession } from '@zhiyue/core';
import React from 'react';
import { investmentApi } from '@/services/bff/investment.api';
import { hasOAuth2Session } from '@/services/oauth2';
import {
  type InvestmentAction,
  initialInvestmentState,
  investmentReducer,
} from './investmentReducer';
import type {
  DecisionListQuery,
  FollowUp,
  InvestmentDashboard,
  InvestmentDecision,
  InvestmentProject,
  InvestmentState,
  Lead,
  LeadListQuery,
  ProjectListQuery,
  Transaction,
  TransactionListQuery,
} from './types';
import { shiftLeadStatusCounts } from './workflow';

export type { InvestmentAction } from './investmentReducer';
export { initialInvestmentState, investmentReducer } from './investmentReducer';

export interface LeadListMeta {
  total: number;
  page: number;
  pageSize: number;
  statusCounts: Record<string, number>;
}

const defaultLeadListMeta: LeadListMeta = {
  total: 0,
  page: 1,
  pageSize: 10,
  statusCounts: {},
};

interface InvestmentStoreValue {
  state: InvestmentState;
  dispatch: (action: InvestmentAction) => void | Promise<void>;
  apiReady: boolean;
  leadListMeta: LeadListMeta;
  dashboardData: InvestmentDashboard | null;
  dashboardLoading: boolean;
  refreshFromApi: () => Promise<void>;
  fetchLeads: (
    query?: LeadListQuery,
  ) => Promise<LeadListMeta & { list: Lead[] }>;
  loadDashboard: (query?: {
    manager?: string;
    industry?: string;
    source?: string;
    fund?: string;
  }) => Promise<InvestmentDashboard | null>;
  loadLeadDetail: (
    leadId: string,
  ) => Promise<{ lead: Lead; followUps: FollowUp[] } | null>;
  loadProjectDetail: (
    projectId: string,
  ) => Promise<{ project: InvestmentProject } | null>;
  loadDecisionDetail: (
    decisionId: string,
  ) => Promise<{ decision: InvestmentDecision } | null>;
  loadTransactionDetail: (
    transactionId: string,
  ) => Promise<{ transaction: Transaction } | null>;
  fetchProjects: (query?: ProjectListQuery) => Promise<InvestmentProject[]>;
  fetchDecisions: (query?: DecisionListQuery) => Promise<InvestmentDecision[]>;
  fetchTransactions: (query?: TransactionListQuery) => Promise<Transaction[]>;
}

const InvestmentContext = React.createContext<InvestmentStoreValue | null>(
  null,
);

const loadInvestmentFromApi = async (): Promise<{
  payload: Partial<InvestmentState>;
  leadListMeta: LeadListMeta;
}> => {
  const [leadResult, projects, decisions, transactions] = await Promise.all([
    investmentApi.listLeads({ page: 1, pageSize: 100 }),
    investmentApi.listProjects(),
    investmentApi.listDecisions(),
    investmentApi.listTransactions(),
  ]);
  const projectList = projects ?? [];
  const decisionList = decisions ?? [];
  const [diligenceNested, riskNested, conditionNested] = await Promise.all([
    Promise.all(
      projectList.map((project) =>
        investmentApi.listDiligenceItems(project.id),
      ),
    ),
    Promise.all(
      projectList.map((project) =>
        investmentApi.listProjectRiskItems(project.id),
      ),
    ),
    Promise.all(
      decisionList.map((decision) =>
        investmentApi.listDecisionConditions(decision.id),
      ),
    ),
  ]);
  return {
    payload: {
      leads: leadResult?.list ?? [],
      projects: projectList,
      decisions: decisionList,
      transactions: transactions ?? [],
      diligenceItems: diligenceNested.flat(),
      risks: riskNested.flat(),
      conditions: conditionNested.flat(),
    },
    leadListMeta: {
      total: leadResult?.total ?? 0,
      page: leadResult?.page ?? 1,
      pageSize: leadResult?.pageSize ?? 100,
      statusCounts: leadResult?.statusCounts ?? {},
    },
  };
};

export const InvestmentProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const [state, baseDispatch] = React.useReducer(
    investmentReducer,
    initialInvestmentState,
  );
  const [apiReady, setApiReady] = React.useState(false);
  const [leadListMeta, setLeadListMeta] =
    React.useState<LeadListMeta>(defaultLeadListMeta);
  const [dashboardData, setDashboardData] =
    React.useState<InvestmentDashboard | null>(null);
  const [dashboardLoading, setDashboardLoading] = React.useState(false);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const refreshFromApi = React.useCallback(async () => {
    const { payload, leadListMeta: meta } = await loadInvestmentFromApi();
    baseDispatch({ type: 'hydrate', payload });
    setLeadListMeta(meta);
    setApiReady(true);
  }, []);

  const fetchLeads = React.useCallback(async (query: LeadListQuery = {}) => {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? 10;
    const result = await investmentApi.listLeads({
      page,
      pageSize,
      ...query,
    });
    const meta = {
      total: result?.total ?? 0,
      page: result?.page ?? page,
      pageSize: result?.pageSize ?? pageSize,
      statusCounts: result?.statusCounts ?? {},
      list: result?.list ?? [],
    };
    setLeadListMeta(meta);
    return meta;
  }, []);

  const loadLeadDetail = React.useCallback(async (leadId: string) => {
    const [lead, followUps] = await Promise.all([
      investmentApi.getLead(leadId),
      investmentApi.listLeadFollowUps(leadId),
    ]);
    baseDispatch({ type: 'updateLead', lead });
    baseDispatch({ type: 'setLeadFollowUps', leadId, followUps });
    return { lead, followUps };
  }, []);

  const loadProjectDetail = React.useCallback(async (projectId: string) => {
    const [project, diligenceItems, risks] = await Promise.all([
      investmentApi.getProject(projectId),
      investmentApi.listDiligenceItems(projectId),
      investmentApi.listProjectRiskItems(projectId),
    ]);
    baseDispatch({ type: 'updateProject', project });
    baseDispatch({
      type: 'hydrate',
      payload: {
        diligenceItems: [
          ...stateRef.current.diligenceItems.filter(
            (item) => item.projectId !== projectId,
          ),
          ...diligenceItems,
        ],
        risks: [
          ...stateRef.current.risks.filter(
            (item) => item.projectId !== projectId,
          ),
          ...risks,
        ],
      },
    });
    return { project };
  }, []);

  const loadDecisionDetail = React.useCallback(async (decisionId: string) => {
    const [decision, conditions] = await Promise.all([
      investmentApi.getDecision(decisionId),
      investmentApi.listDecisionConditions(decisionId),
    ]);
    baseDispatch({ type: 'updateDecision', decision });
    baseDispatch({
      type: 'hydrate',
      payload: {
        conditions: [
          ...stateRef.current.conditions.filter(
            (item) => item.decisionId !== decisionId,
          ),
          ...conditions,
        ],
      },
    });
    return { decision };
  }, []);

  const loadDashboard = React.useCallback(
    async (query?: {
      manager?: string;
      industry?: string;
      source?: string;
      fund?: string;
    }) => {
      setDashboardLoading(true);
      try {
        const dashboard = await investmentApi.getDashboard(query);
        setDashboardData(dashboard);
        baseDispatch({
          type: 'hydrate',
          payload: { investedAmount: dashboard.metrics.investedAmount },
        });
        return dashboard;
      } finally {
        setDashboardLoading(false);
      }
    },
    [],
  );

  const fetchProjects = React.useCallback(
    async (query: ProjectListQuery = {}) =>
      (await investmentApi.listProjects(query)) ?? [],
    [],
  );

  const fetchDecisions = React.useCallback(
    async (query: DecisionListQuery = {}) =>
      (await investmentApi.listDecisions(query)) ?? [],
    [],
  );

  const fetchTransactions = React.useCallback(
    async (query: TransactionListQuery = {}) =>
      (await investmentApi.listTransactions(query)) ?? [],
    [],
  );

  const loadTransactionDetail = React.useCallback(
    async (transactionId: string) => {
      const transaction = await investmentApi.getTransaction(transactionId);
      baseDispatch({ type: 'updateTransaction', transaction });
      return { transaction };
    },
    [],
  );

  React.useEffect(() => {
    if (sessionLoading || !hasOAuth2Session() || !currentUser) {
      return;
    }
    void refreshFromApi();
  }, [refreshFromApi, sessionLoading, currentUser]);

  const dispatch = React.useCallback(
    (action: InvestmentAction): void | Promise<void> => {
      return (async () => {
        switch (action.type) {
          case 'addLead': {
            const lead = await investmentApi.createLead(action.lead);
            baseDispatch({ type: 'addLead', lead });
            setLeadListMeta((prev) => ({
              ...prev,
              total: prev.total + 1,
              statusCounts: {
                ...prev.statusCounts,
                [lead.status]: (prev.statusCounts[lead.status] ?? 0) + 1,
              },
            }));
            break;
          }
          case 'updateLead': {
            const lead = await investmentApi.updateLead(
              action.lead.id,
              action.lead,
            );
            baseDispatch({ type: 'updateLead', lead });
            break;
          }
          case 'deleteLead': {
            const removed = stateRef.current.leads.find(
              (item) => item.id === action.leadId,
            );
            await investmentApi.deleteLead(action.leadId);
            baseDispatch(action);
            setLeadListMeta((prev) => ({
              ...prev,
              total: Math.max(0, prev.total - 1),
              statusCounts: removed
                ? shiftLeadStatusCounts(prev.statusCounts, removed.status)
                : prev.statusCounts,
            }));
            break;
          }
          case 'updateLeadStatus': {
            const previous = stateRef.current.leads.find(
              (item) => item.id === action.leadId,
            );
            const lead = await investmentApi.updateLeadStatus(
              action.leadId,
              action.status,
            );
            baseDispatch({ type: 'updateLead', lead });
            if (previous && previous.status !== lead.status) {
              setLeadListMeta((prev) => ({
                ...prev,
                statusCounts: shiftLeadStatusCounts(
                  prev.statusCounts,
                  previous.status,
                  lead.status,
                ),
              }));
            }
            break;
          }
          case 'addFollowUp': {
            if (!action.leadId) {
              baseDispatch(action);
              break;
            }
            const previous = stateRef.current.leads.find(
              (item) => item.id === action.leadId,
            );
            const followUp = await investmentApi.createLeadFollowUp(
              action.leadId,
              { content: action.content },
            );
            const lead = await investmentApi.getLead(action.leadId);
            baseDispatch({
              type: 'addFollowUp',
              leadId: action.leadId,
              projectId: action.projectId,
              content: action.content,
              followUp,
            });
            baseDispatch({ type: 'updateLead', lead });
            if (previous && previous.status !== lead.status) {
              setLeadListMeta((prev) => ({
                ...prev,
                statusCounts: shiftLeadStatusCounts(
                  prev.statusCounts,
                  previous.status,
                  lead.status,
                ),
              }));
            }
            break;
          }
          case 'convertLead': {
            const previous = stateRef.current.leads.find(
              (item) => item.id === action.leadId,
            );
            const project = await investmentApi.convertLead(action.leadId);
            const lead = await investmentApi.getLead(action.leadId);
            baseDispatch({ type: 'updateLead', lead });
            baseDispatch({ type: 'updateProject', project });
            if (previous && previous.status !== lead.status) {
              setLeadListMeta((prev) => ({
                ...prev,
                statusCounts: shiftLeadStatusCounts(
                  prev.statusCounts,
                  previous.status,
                  lead.status,
                ),
              }));
            }
            break;
          }
          case 'patchProject': {
            const project = await investmentApi.updateProject(
              action.projectId,
              action.payload,
            );
            baseDispatch({ type: 'updateProject', project });
            break;
          }
          case 'updateProjectStage': {
            const project = await investmentApi.updateProjectStage(
              action.projectId,
              action.stage,
            );
            baseDispatch({ type: 'updateProject', project });
            break;
          }
          case 'submitDecision': {
            const decision = await investmentApi.submitDecision(
              action.projectId,
              action.payload,
            );
            baseDispatch({ type: 'updateDecision', decision });
            const project = await investmentApi.getProject(action.projectId);
            baseDispatch({ type: 'updateProject', project });
            break;
          }
          case 'updateTransactionProgress': {
            const transaction = await investmentApi.updateTransactionProgress(
              action.transactionId,
              action.payload,
            );
            baseDispatch({ type: 'updateTransaction', transaction });
            break;
          }
          case 'setDecisionResult': {
            const decision = await investmentApi.setDecisionResult(
              action.decisionId,
              {
                status: action.result,
                conditions: action.conditions,
              },
            );
            baseDispatch({ type: 'updateDecision', decision });
            const [project, transactions] = await Promise.all([
              investmentApi.getProject(decision.projectId),
              investmentApi.listTransactions(),
            ]);
            baseDispatch({ type: 'updateProject', project });
            baseDispatch({ type: 'hydrate', payload: { transactions } });
            break;
          }
          case 'completeClosing': {
            const response = await investmentApi.completeClosing(
              action.transactionId,
              action.cashWaiver ? { cashWaiver: action.cashWaiver } : undefined,
            );
            baseDispatch({
              type: 'updateTransaction',
              transaction: response.transaction,
            });
            const project = await investmentApi.getProject(
              response.transaction.projectId,
            );
            baseDispatch({ type: 'updateProject', project });
            break;
          }
          default:
            baseDispatch(action);
        }
      })();
    },
    [],
  );

  const value = React.useMemo(
    () => ({
      state,
      dispatch,
      apiReady,
      leadListMeta,
      dashboardData,
      dashboardLoading,
      refreshFromApi,
      fetchLeads,
      loadDashboard,
      loadLeadDetail,
      loadProjectDetail,
      loadDecisionDetail,
      loadTransactionDetail,
      fetchProjects,
      fetchDecisions,
      fetchTransactions,
    }),
    [
      state,
      dispatch,
      apiReady,
      leadListMeta,
      dashboardData,
      dashboardLoading,
      refreshFromApi,
      fetchLeads,
      loadDashboard,
      loadLeadDetail,
      loadProjectDetail,
      loadDecisionDetail,
      loadTransactionDetail,
      fetchProjects,
      fetchDecisions,
      fetchTransactions,
    ],
  );
  return (
    <InvestmentContext.Provider value={value}>
      {children}
    </InvestmentContext.Provider>
  );
};

export const useInvestmentStore = (): InvestmentStoreValue => {
  const value = React.useContext(InvestmentContext);
  if (!value)
    throw new Error(
      'useInvestmentStore must be used inside InvestmentProvider',
    );
  return value;
};
